"use client";

import { Store, TriangleAlert } from "lucide-react";
import { useTranslations } from "next-intl";
import { useId } from "react";
import { Button } from "@/components/ui/design-system/button";
import { EmptyState } from "@/components/ui/design-system/empty-state";
import { Skeleton } from "@/components/ui/design-system/skeleton";
import { Hint } from "@/components/ui/design-system/text";
import { cn } from "@/lib/utils";
import { QUIET_OUTLINE } from "../one/chrome";
import { BrandCard } from "./BrandCard";
import { useBrandDirectory } from "./useBrandDirectory";

/**
 * Every brand taking creators, and where this creator stands with each.
 *
 * Rendered in two places: the auth gate, for someone who has signed the
 * agreement and is not on any brand yet, and `/one/brands`, for a creator who
 * already is. The list is the same in both; only what surrounds it differs.
 *
 * How many applications may wait at once comes from the server rather than
 * the shared constant, so the line above the list and the refusal the server
 * would give can never disagree.
 */
export function BrandDirectory({
  columns = 1,
}: {
  /** Two columns past rail width, for the page inside the portal shell. */
  columns?: 1 | 2;
}) {
  const t = useTranslations("brands");
  const shell = useTranslations("shell");
  const limitId = useId();
  const { directory, apply, withdraw, busy, applying, failureFor } =
    useBrandDirectory();
  const grid = cn(
    "m-0 grid list-none gap-nf-ds-item p-0",
    columns === 2 && "lg:grid-cols-2",
  );

  if (directory.isPending) {
    return (
      <div className="flex flex-col gap-nf-ds-block">
        <output className="sr-only">{shell("loading")}</output>
        <Skeleton className="h-nf-ds-4 w-2/3" />
        <ul className={grid}>
          {[0, 1, 2].map((index) => (
            <li key={index}>
              <Skeleton className="h-nf-ds-24 w-full rounded-nf-ds-card" />
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (directory.isError) {
    return (
      <EmptyState
        tone="warn"
        icon={<TriangleAlert className="size-nf-ds-5" />}
        title={t("loadFailed.title")}
        body={t("loadFailed.body")}
        action={
          <Button
            variant="outline"
            size="sm"
            className={QUIET_OUTLINE}
            onClick={() => void directory.refetch()}
          >
            {t("loadFailed.action")}
          </Button>
        }
      />
    );
  }

  const { brands, openApplications, maxOpenApplications } = directory.data;

  if (!brands.length) {
    return (
      <EmptyState
        tone="neutral"
        icon={<Store className="size-nf-ds-5" />}
        title={t("empty.title")}
        body={t("empty.body")}
        action={
          <Button
            variant="outline"
            size="sm"
            className={QUIET_OUTLINE}
            disabled={directory.isFetching}
            onClick={() => void directory.refetch()}
          >
            {t("empty.action")}
          </Button>
        }
      />
    );
  }

  const full = openApplications >= maxOpenApplications;

  return (
    <div className="flex flex-col gap-nf-ds-block">
      {/* Announced when it changes, so a withdrawal that frees a slot is
          heard as well as seen. Muted until it is the reason Apply is off. */}
      <div aria-live="polite">
        <Hint id={limitId} as="p" muted={!full} className="text-pretty">
          {full
            ? t("limit.full", { max: maxOpenApplications })
            : openApplications > 0
              ? t("limit.some", {
                  count: openApplications,
                  max: maxOpenApplications,
                })
              : t("limit.none", { max: maxOpenApplications })}
        </Hint>
      </div>
      <ul className={grid}>
        {brands.map((brand) => (
          <BrandCard
            key={brand.orgSlug}
            brand={brand}
            busy={busy(brand)}
            slotsFull={full}
            applyPending={applying}
            limitId={limitId}
            failure={failureFor(brand)}
            onApply={() => apply(brand)}
            onWithdraw={() => withdraw(brand)}
          />
        ))}
      </ul>
    </div>
  );
}
